import { RootState, ThunkDispatch } from "core-integration/src/store";
import {
  getEntry,
  getEntryEventPoints,
} from "core-integration/src/store/entries/reducers";
import { fetchEntrySummary } from "core-integration/src/store/entries/thunks";
import {
  getCurrentEvent,
  getEventsById,
} from "core-integration/src/store/events/reducers";
import { getRules } from "core-integration/src/store/game/reducers";
import { IRules } from "core-integration/src/store/game/types";
import { getPlayerData } from "core-integration/src/store/player/reducers";
import { IPlayer } from "core-integration/src/store/player/types";
import range from "lodash/range";
import { hideVisually, size } from "polished";
import * as React from "react";
import { useEffect } from "react";
import { useTranslation } from "react-i18next";
import { useDispatch, useSelector } from "react-redux";
import { Link, useParams } from "react-router";
import { Box } from "rebass/styled-components";
import styled, { css } from "styled-components";
import BaseDreamTeam from "../../img/icons/dreamteam.svg?react";
import BaseReload from "../../img/icons/reload.svg?react";
import { ChipName, getChipName } from "../../utils/chips";
import { isRTL } from "../../utils/locale";
import ButtonLink from "../ButtonLink";
import EcalBanner from "../EcalBanner";
import Entry from "../Entry";
import Fixtures from "../Fixtures";
import { ArrowLeft, ArrowRight, ControlArrowRight } from "../icons/Arrows";
import { Main, Secondary, Wrapper } from "../Layout";
import {
  EventPager,
  PagerButton,
  PagerButtonNext,
  PagerHeading,
} from "../Pager";
import Panel from "../Panel";
import { ElementRow } from "../Pitch";
import { NotFound } from "../Routes";
import Table from "../Table";
import TabPanel from "../tabs/TabPanel";
import Tabs from "../tabs/Tabs";
import Title from "../Title";
import Bench from "./Bench";
import BenchUnit from "./BenchUnit";
import ElementExplainDialog from "./ElementExplainDialog";
import EntryEventTable from "./EntryEventTable";
import useEntryEventPitch from "./hooks/useEntryEventPitch";
import PitchFormation from "./PitchFormation";

const StyledArrowRight = styled(ArrowRight)`
  ${size(15, "auto")};
`;
const StyledArrowLeft = styled(ArrowLeft)`
  ${size(15, "auto")};
`;

const PointsScoreboardWrap = styled.div`
  margin: ${({ theme }) => theme.space[2]} 0;
  padding: ${({ theme }) => theme.space[3]} ${({ theme }) => theme.space[1]} 0;
  border: ${({ theme }) => theme.borders[0]};
  border-radius: ${({ theme }) => theme.radii[3]};
  background-color: ${({ theme }) => theme.colors.white4};
`;

const StyledPitchWrapper = styled(Box)`
  border: ${({ theme }) => theme.borders[0]};
  border-radius: ${({ theme }) => theme.radii[3]};
  background-color: ${({ theme }) => theme.colors.white4};
  backdrop-filter: ${({ theme }) => theme.backdropFilters.default};
`;

const PointsScoreboard = styled.div`
  display: flex;
  justify-content: space-between;
  align-items: stretch;
  margin: ${({ theme }) => theme.space[3]} ${({ theme }) => theme.space[2]};
  padding: ${({ theme }) => theme.space[2]};
  border-radius: ${({ theme }) => theme.radii[3]};
  background-color: ${({ theme }) => theme.colors.white4};
  backdrop-filter: ${({ theme }) => theme.backdropFilters.default};
`;

const ScoreboardPrimary = styled.div`
  flex: 0 0 38%;
  padding-inline-end: ${({ theme }) => theme.space[1]};
  padding-inline-start: ${({ theme }) => theme.space[1]};

  @media (min-width: ${({ theme }) => theme.breakpoints[2]}) {
    flex: 0 0 34%;
  }

  @media (min-width: ${({ theme }) => theme.breakpoints[4]}) {
    flex-basis: calc(22% - ${({ theme }) => theme.space[1]});
    margin-bottom: 0;
  }
`;

interface IPrimaryPanelProps {
  chipPlayed?: boolean;
}

const PrimaryPanel = styled.div<IPrimaryPanelProps>`
  position: relative;
  border-radius: 8px;
  padding-top: ${({ theme }) => theme.space[4]};
  color: ${({ theme }) => theme.colors.white};
  background-color: ${({ theme }) => theme.colors.blue};
  text-align: center;

  @media (min-width: ${({ theme }) => theme.breakpoints[2]}) {
    margin-bottom: 0;
    padding-top: ${({ theme }) => theme.space[4]};
    padding-bottom: ${({ theme }) => theme.space[2]};
  }

  ${(props) =>
    props.chipPlayed &&
    css`
      && {
        padding-top: ${({ theme }) => theme.space[4]};
        padding-bottom: ${({ theme }) => theme.space[1]};
      }
    `}
`;

const PrimaryHeading = styled.h4`
  margin: 0;
  padding: ${({ theme }) => theme.space[3]};
  font-size: ${({ theme }) => theme.fontSizes[0]};
  font-weight: 400;

  @media (min-width: ${({ theme }) => theme.breakpoints[2]}) {
    padding-top: 0.8rem;
    padding-bottom: 0.7rem;
  }
`;

const PrimaryValue = styled.div`
  padding-top: ${({ theme }) => theme.space[0]};
  color: ${({ theme }) => theme.colors.white};
  font-size: 4rem;
  font-weight: 700;
  padding-bottom: 1rem;

  @media (min-width: ${({ theme }) => theme.breakpoints[2]}) {
    padding-bottom: 0.3rem;
  }
`;

const ReloadButton = styled.button`
  position: absolute;
  top: 44%;
  right: ${isRTL() && `0`};
  left: ${!isRTL() && `0`};
  transform: ${
    isRTL() ? `translateY(-50%) scaleX(-1) ` : `translateY(-50%) scaleX(1) `
  };
  padding: ${({ theme }) => theme.space[1]};
  border: 0;
  border-radius: ${({ theme }) => theme.radii[0]};
  background-color: transparent;
  line-height: 0;
  cursor: pointer;
`;

const Reload = styled(BaseReload)`
  fill: white;
`;

const ReloadButtonText = styled.span`
  ${hideVisually()}
`;

const ScoreboardSecondary = styled.div`
  display: flex;
  align-items: center;
  width: 100%;
`;

const SecondaryPanel = styled.div`
  flex: 1;
  display: flex;
  flex-direction: column;
  gap: 2rem;

  @media (min-width: ${({ theme }) => theme.breakpoints[4]}) {
    display: flex;
    flex-direction: row;
    justify-content: space-around;
  }
`;

const SecondaryItem = styled.div`
  display: flex;
  flex-direction: column;
  align-items: center;
  overflow: auto;
`;

const SecondaryHeading = styled.h4`
  margin: 0;
  font-family: ${({ theme }) => theme.fonts.base};
  font-size: ${({ theme }) => theme.fontSizes[1]};
  font-weight: normal;
`;

const SecondaryValue = styled.div`
  padding-bottom: 0.7rem;
  font-size: ${({ theme }) => theme.fontSizes[5]};
  color: ${({ theme }) => theme.colors.white1};

  @media (min-width: ${({ theme }) => theme.breakpoints[4]}) {
    font-size: ${({ theme }) => theme.fontSizes[8]};
  }
`;

const DreamTeamWrap = styled.div`
  width: 40%;
  margin: 0 auto ${({ theme }) => theme.space[2]};

  @media (min-width: ${({ theme }) => theme.breakpoints[1]}) {
    width: 25%;
  }

  @media (min-width: ${({ theme }) => theme.breakpoints[3]}) {
    width: 30%;
  }

  @media (min-width: ${({ theme }) => theme.breakpoints[4]}) {
    width: 20%;
  }
`;

const DreamTeamLinkIcon = styled(BaseDreamTeam)`
  ${size(16)}
  margin-inline-end: ${({ theme }) => theme.space[1]};
`;

const DreamTeamLink = styled(Link)`
  display: flex;
  align-items: center;
  justify-content: space-around;
  padding: 0.6rem;
  background-color: transparent;
  color: ${({ theme }) => theme.colors.white1};
  text-decoration: none;
`;

const ChipStatus = styled.div`
  margin: 0 ${({ theme }) => theme.space[1]};
  padding: ${({ theme }) => theme.space[1]};
  @media (min-width: ${({ theme }) => theme.breakpoints[4]}) {
    padding: ${({ theme }) => theme.space[2]};
  }
  border-radius: 5px;
  background-color: ${({ theme }) => theme.colors.white16};
  color: ${({ theme }) => theme.colors.white};
  font-size: 1.1rem;
  font-weight: bold;
  text-align: center;
`;

const EntryEvent = () => {
  const { t } = useTranslation();
  const dispatch = useDispatch<ThunkDispatch>();
  const params = useParams();
  const entryId = Number(params.entryId);
  const eventId = Number(params.eventId);
  const player = useSelector(getPlayerData) as IPlayer;

  const entry = useSelector((state: RootState) => getEntry(state, entryId));

  const event = useSelector(
    (state: RootState) => getEventsById(state)[eventId],
  );

  const mine = Boolean(player && player.entry && player.entry === entryId);
  const now = useSelector((state: RootState) => getCurrentEvent(state));
  const points = useSelector((state: RootState) =>
    getEntryEventPoints(state, entryId, eventId),
  );
  const rules = useSelector(getRules) as IRules;

  const {
    entryData,
    formation,
    elementsById,
    elementsDataById,
    pickForMenu,
    handleShowMenuForPickElement,
    handleHideMenuForPick,
    renderDreamTeam,
    renderPickValue,
    fetchPitchData,
  } = useEntryEventPitch(entryId, eventId);

  const handleReload = () => {
    fetchPitchData();
    dispatch(fetchEntrySummary(entryId));
  };

  useEffect(() => {
    dispatch(fetchEntrySummary(entryId));
  }, [dispatch, entryId]);

  // Handle unknown and unstarted events
  if (!now || !event || event.id > now.id) {
    return <NotFound />;
  }
  if (!entry || !entryData) {
    return null;
  }

  const chipName = entryData.active_chip
    ? getChipName(entryData.active_chip as ChipName, t)
    : "";

  const benchHeading: Record<string, React.ReactNode> = {
    11: (
      <abbr title={t<string>("entryEvent.goalkeeper.title", "Goalkeeper")}>
        {t("entryEvent.goalkeeper.abbr", "GK")}
      </abbr>
    ),
    12: "1",
    13: "2",
    14: "3",
  };

  const nextEnabled = event.id < now.id || (mine && event.id === now.id);
  const previousEnabled = event.id > entry.started_event;
  const nextUrl =
    mine && event.id === now.id
      ? `/my-team`
      : `/entry/${entry.id}/event/${event.id + 1}`;
  const prevUrl = previousEnabled
    ? `/entry/${entry.id}/event/${event.id - 1}`
    : "";

  // Handle events before the entry started
  return (
    <Wrapper>
      <Main>
        <Box mx={2} mb={4}>
          <Title>
            {t("entryEvent.title", "Points")} - {entry.name}
          </Title>
        </Box>

        {/* ROUND PAGER */}
        <PointsScoreboardWrap>
          <Box>
            {(event.id > entry.started_event || event.id < now.id) && (
              <EventPager>
                <PagerButton>
                  <ButtonLink
                    to={prevUrl}
                    disabled={!previousEnabled}
                    variant="secondary"
                    justifyContent="flexStart"
                  >
                    <StyledArrowLeft />
                    {t("entryEvent.prev", "Previous")}
                  </ButtonLink>
                </PagerButton>

                <Box mx={2}>
                  <PagerHeading>{event.name}</PagerHeading>
                </Box>

                <PagerButtonNext>
                  <ButtonLink
                    to={nextUrl}
                    variant="secondary"
                    justifyContent="flexEnd"
                    disabled={!nextEnabled}
                  >
                    {t("entryEvent.next", "Next")}
                    <StyledArrowRight />
                  </ButtonLink>
                </PagerButtonNext>
              </EventPager>
            )}
          </Box>

          {/* SCOREBOARD */}

          <Box mb={4}>
            <PointsScoreboard>
              <ScoreboardSecondary>
                <SecondaryPanel>
                  <SecondaryItem>
                    <SecondaryValue>
                      {event.average_entry_score || "-"}
                    </SecondaryValue>
                    <SecondaryHeading>
                      {t("entryEvent.average", "Average Pts")}
                    </SecondaryHeading>
                  </SecondaryItem>
                  <SecondaryItem>
                    <SecondaryValue>
                      {event.highest_scoring_entry ? (
                        <Link
                          to={`/entry/${event.highest_scoring_entry}/event/${event.id}`}
                        >
                          {event.highest_score || "???"}
                        </Link>
                      ) : (
                        "-"
                      )}
                    </SecondaryValue>
                    <SecondaryHeading>
                      {t("entryEvent.highest", "Highest Pts")}
                    </SecondaryHeading>
                  </SecondaryItem>
                </SecondaryPanel>
                <ScoreboardPrimary>
                  <Box>
                    <PrimaryPanel chipPlayed={Boolean(chipName)}>
                      <PrimaryValue>
                        {points}
                        {!event.finished && (
                          <ReloadButton onClick={handleReload}>
                            <ReloadButtonText>
                              {t("entryEvent.reload", "Reload Points")}
                            </ReloadButtonText>
                            <Reload />
                          </ReloadButton>
                        )}
                      </PrimaryValue>
                      <PrimaryHeading>
                        {event.finished
                          ? t("entryEvent.finalPoints", "Final Points")
                          : t("entryEvent.latestPoints", "Latest Points")}
                      </PrimaryHeading>
                      {chipName && (
                        <ChipStatus>
                          {t(
                            "entryEvent.ChipStatus.played",
                            "{{ chipName }} played",
                            {
                              chipName: chipName,
                            },
                          )}
                        </ChipStatus>
                      )}
                    </PrimaryPanel>
                  </Box>
                </ScoreboardPrimary>
                <SecondaryPanel>
                  <SecondaryItem>
                    <SecondaryValue>
                      {entryData.entry_history.rank
                        ? entryData.entry_history.rank.toLocaleString()
                        : "-"}
                    </SecondaryValue>
                    <SecondaryHeading>
                      {t("entryEvent.rank", "Round Rank")}
                    </SecondaryHeading>
                  </SecondaryItem>
                  <SecondaryItem>
                    <SecondaryValue>
                      <Link to={`/entry/${entry.id}/transfers`}>
                        {entryData.entry_history.event_transfers}
                      </Link>{" "}
                      {entryData.entry_history.event_transfers_cost
                        ? `(-${entryData.entry_history.event_transfers_cost} pts)`
                        : ""}
                    </SecondaryValue>
                    <SecondaryHeading>
                      {t("entryEvent.transfers", "Transfers")}
                    </SecondaryHeading>
                  </SecondaryItem>
                </SecondaryPanel>
              </ScoreboardSecondary>
            </PointsScoreboard>
            <DreamTeamWrap>
              <DreamTeamLink to={`/dream-team/${event.id}`}>
                <DreamTeamLinkIcon />
                {t("entryEvent.dreamTeam", "Dream Team")}
                <ControlArrowRight />
              </DreamTeamLink>
            </DreamTeamWrap>
          </Box>
        </PointsScoreboardWrap>

        <EcalBanner />
        {/* TEAM */}
        <Box mb={6}>
          <StyledPitchWrapper pt={4}>
            <Box mx={2} mb={2}>
              <Tabs centered>
                <TabPanel
                  label={t("entryEvent.pitchView", "Pitch View")}
                  link="pitch"
                >
                  <Box mx={-2} pt={4}>
                    <PitchFormation
                      chipName={entryData.active_chip}
                      formation={formation!}
                      picks={entryData.picks}
                      renderDreamTeam={renderDreamTeam}
                      renderElementMenu={handleShowMenuForPickElement}
                      renderPickValue={renderPickValue}
                      eventId={eventId}
                    />
                    <Bench>
                      <ElementRow>
                        {range(
                          rules.squad_squadplay,
                          rules.squad_squadsize,
                        ).map((i) => (
                          <BenchUnit
                            key={i}
                            chipName={entryData.active_chip}
                            eventId={eventId}
                            heading={benchHeading[i]}
                            pick={entryData.picks[i]}
                            renderDreamTeam={renderDreamTeam}
                            renderElementMenu={() =>
                              handleShowMenuForPickElement(
                                entryData.picks[i].element,
                              )
                            }
                            renderPickValue={renderPickValue}
                          />
                        ))}
                      </ElementRow>
                    </Bench>
                  </Box>
                </TabPanel>
                <TabPanel
                  label={t("entryEvent.listView", "List View")}
                  link="list"
                >
                  <Box my={4}>
                    <EntryEventTable
                      title={t("entryEvent.starters", "Starters")}
                      picks={entryData.picks.slice(0, rules.squad_squadplay)}
                      renderElementMenu={handleShowMenuForPickElement}
                      chipName={entryData.active_chip}
                      dataById={elementsDataById}
                    />
                    <EntryEventTable
                      title={t("entryEvent.substitutes", "Substitutes")}
                      picks={entryData.picks.slice(rules.squad_squadplay)}
                      renderElementMenu={handleShowMenuForPickElement}
                      chipName={entryData.active_chip}
                      dataById={elementsDataById}
                    />
                  </Box>
                </TabPanel>
              </Tabs>
            </Box>
          </StyledPitchWrapper>
        </Box>
        {entryData.automatic_subs.length > 0 && (
          <Box my={4}>
            <Panel>
              <Panel.Header
                title={t(
                  "entryEvent.autoSubs.title",
                  "Automatic Substitutions",
                )}
              ></Panel.Header>

              <Table>
                <thead>
                  <tr>
                    <th>{t("entryEvent.autoSubs.playerOut", "Player out")}</th>
                    <th>{t("entryEvent.autoSubs.playerIn", "Player in")}</th>
                  </tr>
                </thead>
                <tbody>
                  {entryData.automatic_subs.map((s) => (
                    <tr key={s.element_out}>
                      <td>{elementsById[s.element_out].web_name}</td>
                      <td>{elementsById[s.element_in].web_name}</td>
                    </tr>
                  ))}
                </tbody>
              </Table>
            </Panel>
          </Box>
        )}
        {pickForMenu && (
          <ElementExplainDialog
            elementId={pickForMenu.element}
            eventId={event.id}
            closeDialog={handleHideMenuForPick}
          />
        )}
        <Fixtures eventId={event.id} />
      </Main>
      <Secondary>
        <Entry entryId={entry.id} />
      </Secondary>
    </Wrapper>
  );
};

export default EntryEvent;
